import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SentantiaLogo from '../../components/SentantiaLogo';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function LoginPage() {
  useDocumentTitle('Sign In');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === 'true';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { error: loginError } = await login(email, password);
      if (loginError) {
        setError(loginError.message);
        return;
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      await demoLogin();
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Demo login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // One-click live demo from the marketing homepage (/login?demo=1): skip
  // the form and sign straight into the sample-data demo workspace. The ref
  // guard keeps React 18 Strict Mode's double-invoke from starting it twice.
  const demoStartedRef = useRef(false);
  useEffect(() => {
    if (searchParams.get('demo') !== '1' || demoStartedRef.current) return;
    demoStartedRef.current = true;
    handleDemoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-200 p-4">
      <div className="w-full max-w-md bg-white border border-paper-300 shadow-sm p-10" role="main" aria-label="Sign in">
        <div className="flex flex-col items-center mb-8">
          <SentantiaLogo link={false} size="lg" />
        </div>

        <p className="text-center text-sm text-navy-700 mb-8">
          Sign in to your account
        </p>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {expired && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700" role="alert" aria-live="polite">
              Your session has expired. Please sign in again.
            </div>
          )}
          {error && (
            <div className="rounded bg-red-50 p-3 text-sm text-red-600" role="alert" aria-live="assertive">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-navy-900">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@lawfirm.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border border-paper-300 rounded-none focus:border-forest-500 focus:ring-forest-500 h-11"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-navy-900">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border border-paper-300 rounded-none focus:border-forest-500 focus:ring-forest-500 h-11"
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full h-11 bg-navy-900 hover:bg-navy-800 text-white font-medium rounded-none"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-paper-300" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-3 text-paper-500">or</span>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full h-11 border border-paper-300 text-navy-900 hover:bg-paper-100 rounded-none"
          onClick={handleDemoLogin}
          disabled={isLoading}
        >
          Demo login
        </Button>

        <div className="mt-8 flex items-center justify-between text-sm">
          <a
            href="/register"
            className="text-forest-600 hover:text-forest-700 hover:underline"
          >
            Register
          </a>
          <a
            href="/forgot-password"
            className="text-forest-600 hover:text-forest-700 hover:underline"
          >
            Forgot password?
          </a>
        </div>
      </div>
    </div>
  );
}
